import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "../context/CartContext";
import { BRAND } from "../data/content";

const LINKS = [
  { to: "/shop", label: "Shop" },
  { to: "/about", label: "About" },
  { to: "/glossary", label: "Ingredients" },
  { to: "/faq", label: "FAQ" },
];

export function Navbar() {
  const { count, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const location = useLocation();
  const onDarkHero = false;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMobile(false), [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-cream/80 backdrop-blur-md border-b border-[#E4DDD0]" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-5 sm:px-8 py-4">
        <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-cream ring-1 ring-gold/50 overflow-hidden">
            <img src={BRAND.logo} alt="Cured & Clear emblem" className="h-full w-full object-cover scale-110" />
          </span>
          <span className={`font-serif text-2xl tracking-tight transition-colors ${onDarkHero ? "text-cream" : "text-moss"}`}>Cured <span className="text-gold">&</span> Clear</span>
        </Link>

        <div className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `link-underline font-mono text-xs uppercase tracking-[0.18em] transition-colors ${
                  isActive ? "text-gold" : onDarkHero ? "text-cream/85 hover:text-cream" : "text-moss/80 hover:text-moss"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-4">
          <button
            onClick={() => setOpen(true)}
            className={`relative p-2 transition-colors hover:text-gold ${onDarkHero ? "text-cream" : "text-moss"}`}
            data-testid="nav-cart-button"
            aria-label="Open cart"
          >
            <ShoppingBag className="h-5 w-5" />
            <AnimatePresence>
              {count > 0 && (
                <motion.span
                  key={count}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                  className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-gold text-[10px] font-semibold text-moss-dark"
                  data-testid="nav-cart-count"
                >
                  {count}
                </motion.span>
              )}
            </AnimatePresence>
          </button>
          <button
            className={`md:hidden p-2 ${onDarkHero ? "text-cream" : "text-moss"}`}
            onClick={() => setMobile((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {mobile ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {mobile && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-cream/95 backdrop-blur-md border-b border-[#E4DDD0]"
          >
            <div className="flex flex-col px-6 py-4">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className="py-3 font-serif text-xl text-moss border-b border-[#E4DDD0] last:border-0"
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
